<script setup lang="ts">
/**
 * A panel docked beside the main content — pinned messages, a thread, a file
 * preview, search results. Head with title, optional subtitle, own actions
 * (`actions` slot) and a close button; a body that scrolls on its own; an
 * optional `footer`. When the body is empty, pass `empty` (text) or the
 * `empty` slot and leave the default slot out.
 *
 * It takes the full height of its parent and a fixed width (`width`), with a
 * hairline towards the content (`side` says which edge it docks to). With
 * `width="fill"` the parent sizes it instead (the NxWorkspace aside, a
 * resizable splitter pane) and draws the edge, so the panel has no hairline of
 * its own. Escape inside the panel emits `close`, as the close button does —
 * unless something inside used it first (`preventDefault`: an open popover
 * or list closing), so the innermost popup closes first.
 *
 * With `overlay` it floats over the content instead of taking room beside it
 * (a narrow window): positioned against the nearest positioned ancestor — give
 * the host `position: relative` — full height, docked to `side`, raised with
 * the kit's elevation. Still not modal: no backdrop, the content stays usable;
 * a blocking overlay is NxDrawer.
 */
import X from 'lucide-vue-next/dist/esm/icons/x.js';
import { computed, useId, useSlots } from 'vue';
import { NxIconButton } from '../IconButton';

const props = withDefaults(
  defineProps<{
    title: string;
    subtitle?: string;
    /** Panel width. Defaults to `md`. `fill` takes the parent's width (no own edge line). */
    width?: 'sm' | 'md' | 'lg' | 'fill';
    /** Edge it docks to: `end` (right in LTR, default) or `start`. */
    side?: 'start' | 'end';
    closable?: boolean;
    closeLabel?: string;
    /** Text for an empty body (when there is no default slot content). */
    empty?: string;
    /** Dims the body while it reloads, keeping what it showed. */
    busy?: boolean;
    /** Floats over the host's content instead of pushing it aside (see above). */
    overlay?: boolean;
  }>(),
  {
    subtitle: undefined,
    width: 'md',
    side: 'end',
    closable: true,
    closeLabel: 'Schließen',
    empty: undefined,
    busy: false,
    overlay: false,
  }
);

const emit = defineEmits<{ close: [] }>();
const slots = useSlots();
const titleId = useId();

const showEmpty = computed(() => !slots.default && (props.empty || slots.empty));

function onKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape' || !props.closable) return;
  // Something inside already used this Escape — an open popover or list closed
  // on it — so the innermost popup closes first and the panel stays.
  if (event.defaultPrevented) return;
  event.stopPropagation();
  emit('close');
}
</script>

<template>
  <aside
    class="nx-side-panel"
    :class="[`width-${props.width}`, `side-${props.side}`, { 'is-overlay': props.overlay }]"
    :aria-labelledby="titleId"
    data-testid="nx-side-panel"
    @keydown="onKeydown"
  >
    <header class="nx-side-panel__head">
      <div class="nx-side-panel__titles">
        <h2 :id="titleId" class="nx-side-panel__title">{{ props.title }}</h2>
        <p v-if="props.subtitle" class="nx-side-panel__subtitle">{{ props.subtitle }}</p>
      </div>
      <div v-if="$slots.actions || props.closable" class="nx-side-panel__actions">
        <slot name="actions" />
        <NxIconButton
          v-if="props.closable"
          height="s"
          :title="props.closeLabel"
          :aria-label="props.closeLabel"
          data-testid="nx-side-panel-close"
          @click="emit('close')"
        >
          <X class="nxt-icon-md" aria-hidden="true" />
        </NxIconButton>
      </div>
    </header>
    <div class="nx-side-panel__body" :class="{ 'is-busy': props.busy }" :aria-busy="props.busy">
      <slot />
      <div v-if="showEmpty" class="nx-side-panel__empty">
        <slot name="empty">{{ props.empty }}</slot>
      </div>
    </div>
    <footer v-if="$slots.footer" class="nx-side-panel__foot">
      <slot name="footer" />
    </footer>
  </aside>
</template>

<style scoped>
.nx-side-panel {
  --side-panel-width: var(--nxt-panel-width-xl);

  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  width: var(--side-panel-width);
  max-width: 100%;
  height: 100%;
  min-height: 0;
  background: var(--nxt-surface-bg);
}
.nx-side-panel.width-sm {
  --side-panel-width: var(--nxt-panel-width-lg);
}
.nx-side-panel.width-lg {
  --side-panel-width: var(--nxt-panel-width-3xl);
}
.nx-side-panel.width-fill {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
}
.nx-side-panel.side-end:not(.width-fill) {
  border-inline-start: var(--nxt-hairline) solid var(--nxt-border);
}
.nx-side-panel.side-start:not(.width-fill) {
  border-inline-end: var(--nxt-hairline) solid var(--nxt-border);
}
/* Overlay: over the host's content, docked to its side, raised. */
.nx-side-panel.is-overlay {
  position: absolute;
  inset-block: 0;
  z-index: var(--nxt-z-float);
  height: auto;
  box-shadow: var(--nxt-shadow-lg);
}
.nx-side-panel.is-overlay.side-end {
  inset-inline-end: 0;
}
.nx-side-panel.is-overlay.side-start {
  inset-inline-start: 0;
}
.nx-side-panel__head {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: var(--nxt-space-sm);
  min-height: var(--nxt-control-size-xl);
  padding: var(--nxt-space-xs) var(--nxt-space-sm) var(--nxt-space-xs) var(--nxt-space-md);
  border-block-end: var(--nxt-hairline) solid var(--nxt-border);
}
.nx-side-panel__titles {
  flex: 1 1 auto;
  min-width: 0;
}
.nx-side-panel__title {
  margin: 0;
  overflow: hidden;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-md);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: var(--nxt-line-height-tight);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-side-panel__subtitle {
  margin: var(--nxt-space-2xs) 0 0;
  overflow: hidden;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-side-panel__actions {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: var(--nxt-space-2xs);
}
.nx-side-panel__body {
  flex: 1 1 auto;
  min-height: 0;
  padding: var(--nxt-space-sm);
  overflow-y: auto;
  overscroll-behavior: contain;
  transition: opacity var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-side-panel__body.is-busy {
  opacity: 0.55;
}
.nx-side-panel__empty {
  padding: var(--nxt-space-xl) var(--nxt-space-md);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
  text-align: center;
}
.nx-side-panel__foot {
  flex: 0 0 auto;
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  border-block-start: var(--nxt-hairline) solid var(--nxt-border);
}
@media (prefers-reduced-motion: reduce) {
  .nx-side-panel__body {
    transition: none;
  }
}
</style>
